import { Component } from '@theme/component';

/**
 * Highlights the active letter in the sticky alphabet nav while scrolling
 * and provides smooth scroll on click. Mobile uses native <details> accordions
 * so this controller is desktop-only behavior.
 */
class AlphabeticalSection extends Component {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {HTMLAnchorElement[]} */
  #navLinks = [];

  /** @type {Map<string, HTMLAnchorElement>} */
  #linksByLetter = new Map();

  connectedCallback() {
    super.connectedCallback();

    this.#navLinks = Array.from(this.querySelectorAll('.alphabetical__nav-list a[data-letter]'));
    for (const link of this.#navLinks) {
      const letter = link.dataset.letter;
      if (letter) this.#linksByLetter.set(letter, link);
      link.addEventListener('click', this.#handleNavClick);
    }

    const groups = this.querySelectorAll('.alphabetical__group');
    if (groups.length === 0) return;

    this.#observer = new IntersectionObserver(this.#handleIntersect, {
      // Trigger when group's top crosses just below the sticky nav.
      rootMargin: '-30% 0px -65% 0px',
      threshold: 0,
    });

    for (const group of groups) this.#observer.observe(group);

    // Initial highlight: first nav link.
    if (this.#navLinks[0]) this.#setCurrent(this.#navLinks[0]);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    for (const link of this.#navLinks) link.removeEventListener('click', this.#handleNavClick);
  }

  /** @param {IntersectionObserverEntry[]} entries */
  #handleIntersect = (entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      const letter = /** @type {HTMLElement} */ (entry.target).dataset.letter;
      const link = letter ? this.#linksByLetter.get(letter) : null;
      if (link) this.#setCurrent(link);
    }
  };

  /** @param {HTMLAnchorElement} link */
  #setCurrent(link) {
    for (const l of this.#navLinks) l.removeAttribute('data-current');
    link.setAttribute('data-current', '');
  }

  /** @param {Event} event */
  #handleNavClick = (event) => {
    const link = /** @type {HTMLAnchorElement} */ (event.currentTarget);
    const target = document.getElementById(decodeURIComponent(link.hash.slice(1)));
    if (!target) return;

    event.preventDefault();
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    history.replaceState(null, '', link.hash);
    this.#setCurrent(link);
  };
}

if (!customElements.get('alphabetical-section')) {
  customElements.define('alphabetical-section', AlphabeticalSection);
}
